import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GET_MATCHING_SUGGESTIONS, CREATE_MATCHING, SKIP_USER, CLEAR_SKIPPED_USERS } from '../graphql/matching';
import { GET_PENDING_REQUESTS, ACCEPT_FRIEND_REQUEST, REJECT_FRIEND_REQUEST } from '../graphql/friends';
import { UPDATE_ONLINE_STATUS } from '../graphql/user';
import { useAuth } from '../contexts/AuthContext';
import { FiHeart, FiMessageSquare, FiEye, FiCheckCircle, FiXCircle, FiSend, FiInbox, FiClock } from 'react-icons/fi';
import UserDetailModal from '../components/UserDetailModal';

const Matching = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const client = useApolloClient();

  const validTabs = ['send', 'respond', 'sent'];
  const tabParam = searchParams.get('tab');
  const initialTab = validTabs.includes(tabParam) ? tabParam : 'send';

  const [activeTab, setActiveTab] = useState(initialTab); // 'send', 'respond', or 'sent'
  const [selectedUserId, setSelectedUserId] = useState(null);

  // 同步 URL tab 與本地狀態
  useEffect(() => {
    if (tabParam && validTabs.includes(tabParam) && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
    if (!tabParam && activeTab !== 'send') {
      setActiveTab('send');
    }
  }, [tabParam, activeTab]);

  // URL 同步：讀取和管理 userId 參數
  useEffect(() => {
    const urlUserId = searchParams.get('userId');
    if (urlUserId && urlUserId !== selectedUserId) {
      setSelectedUserId(urlUserId);
    }
    if (!urlUserId && selectedUserId !== null) {
      setSelectedUserId(null);
    }
  }, [searchParams, selectedUserId]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'send') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
      }
      return next;
    });
  };

  // 獲取配對建議
  const { data: suggestionsData, loading: suggestionsLoading, error: suggestionsError } = useQuery(
    GET_MATCHING_SUGGESTIONS,
    {
      variables: { limit: 20 },
      fetchPolicy: 'cache-and-network'
    }
  );

  // 創建配對請求
  const [createMatching, { loading: createLoading }] = useMutation(CREATE_MATCHING);

  // 獲取待處理請求
  const { 
    data: requestsData, 
    loading: requestsLoading, 
    error: requestsError,
    refetch: refetchRequests 
  } = useQuery(GET_PENDING_REQUESTS, {
    skip: !user,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network'
  });

  // 接受好友請求
  const [acceptFriendRequest, { loading: acceptLoading }] = useMutation(ACCEPT_FRIEND_REQUEST, {
    update: (cache, { data }) => {
      try {
        if (!data?.acceptFriendRequest?.success) return;

        const existingData = cache.readQuery({
          query: GET_PENDING_REQUESTS
        });

        if (existingData?.pendingRequests?.relationships) {
          // 過濾掉已接受的請求
          const updatedRelationships = existingData.pendingRequests.relationships.filter(
            req => req.id !== data.acceptFriendRequest.relationship.id
          );

          cache.writeQuery({
            query: GET_PENDING_REQUESTS,
            data: {
              ...existingData,
              pendingRequests: {
                ...existingData.pendingRequests,
                relationships: updatedRelationships
              }
            }
          });

          console.log('快取已更新：移除已接受的請求');
        }
      } catch (error) {
        console.error('接受請求快取更新失敗:', error);
      }
    }
  });

  // 拒絕好友請求
  const [rejectFriendRequest, { loading: rejectLoading }] = useMutation(REJECT_FRIEND_REQUEST);

  // 跳過使用者
  const [skipUser] = useMutation(SKIP_USER);

  // 清空跳過使用者列表
  const [clearSkippedUsers] = useMutation(CLEAR_SKIPPED_USERS, {
    update: (cache, { data }) => {
      try {
        if (!data?.clearSkippedUsers?.success) return;

        // 清空跳過使用者列表後，需要重新獲取匹配建議
        // 因為可能會重新顯示之前跳過的使用者
        const existingData = cache.readQuery({
          query: GET_MATCHING_SUGGESTIONS,
          variables: { limit: 20 }
        });

        if (existingData) {
          // 觸發重新獲取，但使用 network-only 確保獲取最新資料
          console.log('已清空跳過使用者列表，快取將在下次查詢時更新');
        }
      } catch (error) {
        console.error('清空跳過使用者快取更新失敗:', error);
      }
    },
    refetchQueries: [{ query: GET_MATCHING_SUGGESTIONS, variables: { limit: 20 } }]
  });

  // 更新上線狀態
  const [updateOnlineStatus] = useMutation(UPDATE_ONLINE_STATUS);

  // 更新上線狀態
  useEffect(() => {
    if (user && user.id) {
      updateOnlineStatus();
    }
  }, [user, updateOnlineStatus]);

  // 取消已發送的請求（使用 rejectFriendRequest）
  const handleCancelRequest = async (targetUserId, username) => {
    try {
      const result = await rejectFriendRequest({
        variables: { targetUserId }
      });

      if (result.data.rejectFriendRequest.success) {
        // 手動更新快取：從待處理請求中移除已取消的請求
        try {
          const existingData = client.cache.readQuery({
            query: GET_PENDING_REQUESTS
          });

          if (existingData?.pendingRequests?.relationships) {
            const updatedRelationships = existingData.pendingRequests.relationships.filter(
              req => {
                const otherUser = req.userA.id === user?.id ? req.userB : req.userA;
                return otherUser.id !== targetUserId;
              }
            );

            client.cache.writeQuery({
              query: GET_PENDING_REQUESTS,
              data: {
                ...existingData,
                pendingRequests: {
                  ...existingData.pendingRequests,
                  relationships: updatedRelationships
                }
              }
            });

            console.log('快取已更新：移除已取消的請求', targetUserId);
          }
        } catch (error) {
          console.error('更新快取失敗:', error);
          // 如果快取更新失敗，使用 refetch 作為備用方案
          client.cache.evict({ fieldName: 'pendingRequests' });
        }
        // 移除成功提示，只保留錯誤處理
      } else {
        alert('取消失敗：' + result.data.rejectFriendRequest.message);
      }
    } catch (error) {
      console.error('取消配對請求失敗:', error);
      alert('取消配對請求失敗');
    }
  };

  const handleSendRequest = async (targetUserId) => {
    try {
      const result = await createMatching({
        variables: { targetUserId }
      });
      
      if (result.data.createMatching.success) {
        // 手動更新快取：從建議列表中移除已發送請求的使用者，並添加到待處理請求
        try {
          const suggestionsData = client.cache.readQuery({
            query: GET_MATCHING_SUGGESTIONS,
            variables: { limit: 20 }
          });

          if (suggestionsData?.matchingSuggestions?.suggestions) {
            // 找到要發送請求的使用者信息
            const targetSuggestion = suggestionsData.matchingSuggestions.suggestions.find(
              suggestion => suggestion.userId === targetUserId
            );

            // 從建議列表中移除已發送請求的使用者
            const updatedSuggestions = suggestionsData.matchingSuggestions.suggestions.filter(
              suggestion => suggestion.userId !== targetUserId
            );

            const updatedTotalCount = Math.max(0, (suggestionsData.matchingSuggestions.totalCount || 0) - 1);

            // 更新匹配建議快取
            client.cache.writeQuery({
              query: GET_MATCHING_SUGGESTIONS,
              variables: { limit: 20 },
              data: {
                ...suggestionsData,
                matchingSuggestions: {
                  ...suggestionsData.matchingSuggestions,
                  suggestions: updatedSuggestions,
                  totalCount: updatedTotalCount
                }
              }
            });

            console.log('匹配建議快取已更新：移除已發送請求的使用者', targetUserId);

            // 手動添加到待處理請求快取
            if (targetSuggestion) {
              try {
                const requestsData = client.cache.readQuery({
                  query: GET_PENDING_REQUESTS
                });

                if (requestsData?.pendingRequests?.relationships) {
                  // 檢查是否已經存在（避免重複添加）
                  const existingRequest = requestsData.pendingRequests.relationships.find(
                    req => {
                      const otherUser = req.userA.id === user?.id ? req.userB : req.userA;
                      return otherUser.id === targetUserId;
                    }
                  );

                  if (!existingRequest) {
                    // 創建新的已發送請求對象
                    const newRelationship = {
                      id: `temp-${Date.now()}`, // 臨時ID，後端會返回真實ID
                      userA: { id: user?.id, username: user?.username || '' },
                      userB: { id: targetUserId, username: targetSuggestion.username || '' },
                      typeA: 'SENT',
                      typeB: 'RECEIVED',
                      requestDirection: 'SENT',
                      matchingScore: targetSuggestion.score || 0,
                      matchingTotalWeight: targetSuggestion.totalWeight || 0,
                      matchingTotalScore: targetSuggestion.score || 0,
                      matchingAnsweredQuestions: targetSuggestion.answeredQuestions || 0,
                      matchingTotalQuestions: targetSuggestion.totalQuestions || 0,
                      otherUserSelfIntroduction: targetSuggestion.selfIntroduction || '',
                      establishedAt: new Date().toISOString(),
                      status: 'PENDING'
                    };

                    // 添加到現有關係列表
                    const updatedRelationships = [
                      ...requestsData.pendingRequests.relationships,
                      newRelationship
                    ];

                    client.cache.writeQuery({
                      query: GET_PENDING_REQUESTS,
                      data: {
                        ...requestsData,
                        pendingRequests: {
                          ...requestsData.pendingRequests,
                          relationships: updatedRelationships
                        }
                      }
                    });

                    console.log('待處理請求快取已更新：添加新的已發送請求', targetUserId);
                  }
                }
              } catch (error) {
                console.error('更新待處理請求快取失敗:', error);
                // 如果更新失敗，使用 evict 作為備用方案
                client.cache.evict({ fieldName: 'pendingRequests' });
              }
            }
          }
        } catch (error) {
          console.error('更新匹配建議快取失敗:', error);
        }
      } else {
        alert('發送失敗：' + result.data.createMatching.message);
      }
    } catch (error) {
      console.error('發送配對請求失敗:', error);
      alert('發送配對請求失敗');
    }
  };

  const handleViewDetails = (suggestion) => {
    setSelectedUserId(suggestion.userId);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('userId', suggestion.userId);
      return next;
    });
  };

  const handleCloseModal = () => {
    setSelectedUserId(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('userId');
      return next;
    });
  };

  const handleSkipUser = async (userId) => {
    try {
      const result = await skipUser({
        variables: { targetUserId: userId }
      });
      
      if (result.data.skipUser.success) {
        // 手動更新快取：從建議列表中移除已跳過的使用者
        try {
          const existingData = client.cache.readQuery({
            query: GET_MATCHING_SUGGESTIONS,
            variables: { limit: 20 }
          });

          if (existingData?.matchingSuggestions?.suggestions) {
            const updatedSuggestions = existingData.matchingSuggestions.suggestions.filter(
              suggestion => suggestion.userId !== userId
            );

            const updatedTotalCount = Math.max(0, (existingData.matchingSuggestions.totalCount || 0) - 1);

            client.cache.writeQuery({
              query: GET_MATCHING_SUGGESTIONS,
              variables: { limit: 20 },
              data: {
                ...existingData,
                matchingSuggestions: {
                  ...existingData.matchingSuggestions,
                  suggestions: updatedSuggestions,
                  totalCount: updatedTotalCount
                }
              }
            });

            console.log('快取已更新：從匹配建議中移除已跳過的使用者', userId);
          }
        } catch (error) {
          console.error('更新快取失敗:', error);
          // 如果快取更新失敗，使用 refetch 作為備用方案
          client.cache.evict({ fieldName: 'matchingSuggestions' });
        }
      } else {
        alert('跳過失敗：' + result.data.skipUser.message);
      }
    } catch (error) {
      console.error('跳過使用者失敗:', error);
      alert('跳過使用者失敗');
    }
  };

  const handleClearSkippedUsers = async () => {
    if (!window.confirm('確定要取消跳過所有使用者嗎？這將重新顯示之前跳過的使用者。')) {
      return;
    }
    
    try {
      const result = await clearSkippedUsers();
      
      if (result.data.clearSkippedUsers.success) {
        alert(result.data.clearSkippedUsers.message);
        // 成功後會自動 refetch 配對建議
      } else {
        alert('清除失敗：' + result.data.clearSkippedUsers.message);
      }
    } catch (error) {
      console.error('清除跳過使用者列表失敗:', error);
      alert('清除跳過使用者列表失敗');
    }
  };

  const handleAcceptRequest = async (targetUserId) => {
    try {
      const result = await acceptFriendRequest({
        variables: { targetUserId }
      });

      if (result.data.acceptFriendRequest.success) {
        alert('已接受好友請求！');
        // 移除 refetchRequests()，因為已使用 update 函數更新快取
      } else {
        alert('接受失敗：' + result.data.acceptFriendRequest.message);
      }
    } catch (error) {
      console.error('接受好友請求失敗:', error);
      alert('接受好友請求失敗');
    }
  };

  const handleRejectRequest = async (targetUserId) => {
    try {
      const result = await rejectFriendRequest({
        variables: { targetUserId }
      });

      if (result.data.rejectFriendRequest.success) {
        // 手動更新快取：從待處理請求中移除已拒絕的請求
        try {
          const existingData = client.cache.readQuery({
            query: GET_PENDING_REQUESTS
          });

          if (existingData?.pendingRequests?.relationships) {
            const updatedRelationships = existingData.pendingRequests.relationships.filter(
              req => {
                const otherUser = req.userA.id === user?.id ? req.userB : req.userA;
                return otherUser.id !== targetUserId;
              }
            );

            client.cache.writeQuery({
              query: GET_PENDING_REQUESTS,
              data: {
                ...existingData,
                pendingRequests: {
                  ...existingData.pendingRequests,
                  relationships: updatedRelationships
                }
              }
            });

            console.log('快取已更新：移除已拒絕的請求', targetUserId);
          }
        } catch (error) {
          console.error('更新快取失敗:', error);
          // 如果快取更新失敗，使用 refetch 作為備用方案
          client.cache.evict({ fieldName: 'pendingRequests' });
        }
        alert('已拒絕好友請求');
      } else {
        alert('拒絕失敗：' + result.data.rejectFriendRequest.message);
      }
    } catch (error) {
      console.error('拒絕好友請求失敗:', error);
      alert('拒絕好友請求失敗');
    }
  };

  // 提取數據（後端已過濾跳過的使用者）
  const suggestions = suggestionsData?.matchingSuggestions?.suggestions || [];
  const pendingRequests = requestsData?.pendingRequests?.relationships || [];
  
  // 分類待處理請求
  // 收到的請求
  const receivedRequests = pendingRequests
    .filter(req => req.requestDirection === 'RECEIVED' || req.requestDirection === 'MUTUAL')
    .sort((a, b) => (b.matchingScore || 0) - (a.matchingScore || 0));
  
  // 已發送的請求
  const sentRequests = pendingRequests
    .filter(req => req.requestDirection === 'SENT')
    .sort((a, b) => (b.matchingScore || 0) - (a.matchingScore || 0));

  // Loading states
  const isLoading = activeTab === 'send' ? suggestionsLoading : requestsLoading;
  const hasError = activeTab === 'send' ? suggestionsError : requestsError;

  // 獲取自介前5行的輔助函數
  const getSelfIntroductionPreview = (intro) => {
    if (!intro) return '';
    const lines = intro.split('\n').filter(line => line.trim());
    return lines.slice(0, 5).join('\n');
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">載入中...</p>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">載入失敗</h3>
          <p className="text-red-600 mt-1">{hasError.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* 頁面標題 */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 via-yellow-600 to-purple-600 bg-clip-text text-transparent mb-2">配對系統</h1>
        <p className="text-gray-600">基於你的擇友標準，尋找契合的人並發送配對請求</p>
      </div>

      {/* 標籤頁導航 */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-2">
          <button
            onClick={() => handleTabChange('send')}
            className={`py-2 px-2 border-b-2 font-medium text-sm whitespace-nowrap flex items-center ${
              activeTab === 'send'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiSend className="w-4 h-4 mr-2 hidden md:inline" />
            發出請求
            <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
              activeTab === 'send'
                ? 'bg-purple-100 text-purple-600'
                : 'bg-gray-100 text-gray-600'
            }`}>
              {suggestions.length}
            </span>
          </button>
          <button
            onClick={() => handleTabChange('respond')}
            className={`py-2 px-2 border-b-2 font-medium text-sm whitespace-nowrap flex items-center ${
              activeTab === 'respond'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiInbox className="w-4 h-4 mr-2 hidden md:inline" />
            回覆請求
            <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
              activeTab === 'respond'
                ? 'bg-purple-100 text-purple-600'
                : 'bg-gray-100 text-gray-600'
            }`}>
              {receivedRequests.length}
            </span>
          </button>
          <button
            onClick={() => handleTabChange('sent')}
            className={`py-2 px-2 border-b-2 font-medium text-sm whitespace-nowrap flex items-center ${
              activeTab === 'sent'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FiClock className="w-4 h-4 mr-2 hidden md:inline" />
            已發送
            <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
              activeTab === 'sent'
                ? 'bg-purple-100 text-purple-600'
                : 'bg-gray-100 text-gray-600'
            }`}>
              {sentRequests.length}
            </span>
          </button>
        </nav>
      </div>

      {/* 內容區域 */}
      <div className="space-y-6">
        {/* 發出配對請求 Tab */}
        {activeTab === 'send' && (
          <>
            {suggestions.length === 0 ? (
              <div className="text-center py-12">
                <FiHeart className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">暫無配對建議</h3>
                <p className="mt-2 text-gray-600">你或許還沒有建立題庫，去將投票加入題庫吧</p>
              </div>
            ) : (
              suggestions.map((suggestion) => {
                const score = typeof suggestion.score === 'number' ? suggestion.score.toFixed(2) : suggestion.score;
                // 獲取自介前5行
                const getSelfIntroductionPreview = (intro) => {
                  if (!intro) return '';
                  const lines = intro.split('\n').filter(line => line.trim());
                  return lines.slice(0, 5).join('\n');
                };
                const selfIntroPreview = getSelfIntroductionPreview(suggestion.selfIntroduction);
                
                return (
                  <div
                    key={suggestion.userId}
                    className="bg-white hover:bg-gray-50 rounded-lg shadow-md border border-gray-200 p-6 cursor-pointer transition-colors"
                    onClick={() => handleViewDetails(suggestion)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleViewDetails(suggestion);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    {/* 第一行：username | 查看詳情 */}
                    <div className="flex items-center justify-between -mx-6 -mt-6 px-6 pt-6 pb-3 mb-3 border-b border-gray-200 bg-yellow-200 rounded-t-lg">
                      <h3 className="text-lg font-semibold text-gray-900">{suggestion.username}</h3>
                      <div className="flex items-center text-sm text-gray-500">
                        <FiEye className="w-4 h-4 mr-1 hidden md:inline" />
                        <span>查看詳情</span>
                      </div>
                    </div>

                    {/* 第二行：評分 | 跳過和發送配對請求按鈕 */}
                    <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200">
                      <span className="text-purple-600 font-semibold">評分: {score}</span>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            handleSkipUser(suggestion.userId);
                          }}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 flex-shrink-0"
                        >
                          <FiXCircle className="w-4 h-4 mr-1 hidden md:inline" />
                          跳過
                        </button>
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            handleSendRequest(suggestion.userId);
                          }}
                          disabled={createLoading}
                          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-pink-400 to-purple-500 hover:from-pink-500 hover:to-purple-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                        >
                          <FiHeart className="w-4 h-4 mr-2 hidden md:inline" />
                          {createLoading ? '發送中...' : '發送配對請求'}
                        </button>
                      </div>
                    </div>

                    {/* 第三行：自我介紹前5行 */}
                    <div>
                      {selfIntroPreview ? (
                        <div className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-5">
                          {selfIntroPreview}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-400 italic">暫無自我介紹</div>
                      )}
                    </div>
                  </div>
                );
              })
            )}

            {/* 取消跳過使用者按鈕 */}
            <div className="mt-8 text-center">
              <button
                onClick={handleClearSkippedUsers}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                <FiXCircle className="w-4 h-4 mr-2" />
                取消跳過
              </button>
            </div>

            {suggestions.length > 0 && (
              <div className="mt-8 text-center">
                <button
                  onClick={() => navigate('/voting?tab=questions')}
                  className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-md shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <FiMessageSquare className="w-5 h-5 mr-2 hidden md:inline" />
                  不滿意推薦？修改擇友標準
                </button>
              </div>
            )}
          </>
        )}

        {/* 已發送請求 Tab */}
        {activeTab === 'sent' && (
          <>
            {sentRequests.length === 0 ? (
              <div className="text-center py-12">
                <FiClock className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">暫無已發送請求</h3>
                <p className="mt-2 text-gray-600">你還沒有發送任何配對請求</p>
              </div>
            ) : (
              sentRequests.map((request) => {
                // 獲取對方使用者信息
                const otherUser = request.userA.id === user?.id ? request.userB : request.userA;
                const matchingScore = request.matchingScore || 0;
                const selfIntroPreview = getSelfIntroductionPreview(request.otherUserSelfIntroduction);
                
                return (
                  <div
                    key={request.id}
                    className="bg-white hover:bg-gray-50 rounded-lg shadow-md border border-gray-200 p-6 cursor-pointer transition-colors"
                    onClick={() => handleViewDetails({ userId: otherUser.id })}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleViewDetails({ userId: otherUser.id });
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    {/* 第一行：username | 查看詳情 */}
                    <div className="flex items-center justify-between -mx-6 -mt-6 px-6 pt-6 pb-3 mb-3 border-b border-gray-200 bg-yellow-200 rounded-t-lg">
                      <h3 className="text-lg font-semibold text-gray-900">{otherUser.username}</h3>
                      <div className="flex items-center text-sm text-gray-500">
                        <FiEye className="w-4 h-4 mr-1 hidden md:inline" />
                        <span>查看詳情</span>
                      </div>
                    </div>

                    {/* 第二行：評分 | 取消請求按鈕 */}
                    <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200">
                      <span className="text-purple-600 font-semibold">評分: {matchingScore.toFixed(2)}</span>
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          handleCancelRequest(otherUser.id, otherUser.username);
                        }}
                        disabled={rejectLoading}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                      >
                        <FiXCircle className="w-4 h-4 mr-2 hidden md:inline" />
                        {rejectLoading ? '取消中...' : '取消請求'}
                      </button>
                    </div>

                    {/* 第三行：自我介紹前5行 */}
                    <div>
                      {selfIntroPreview ? (
                        <div className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-5">
                          {selfIntroPreview}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-400 italic">暫無自我介紹</div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}

        {/* 回應配對請求 Tab */}
        {activeTab === 'respond' && (
          <>
            {receivedRequests.length === 0 ? (
              <div className="text-center py-12">
                <FiInbox className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">暫無配對請求</h3>
                <p className="mt-2 text-gray-600">多多投票增加吸引力！</p>
              </div>
            ) : (
              receivedRequests.map((request) => {
                // 獲取對方使用者信息
                const otherUser = request.userA.id === user?.id ? request.userB : request.userA;
                const matchingScore = request.matchingScore || 0;
                const score = matchingScore.toFixed(2);
                const selfIntroPreview = getSelfIntroductionPreview(request.otherUserSelfIntroduction);
                
                return (
                  <div
                    key={request.id}
                    className="bg-white hover:bg-gray-50 rounded-lg shadow-md border border-gray-200 p-6 cursor-pointer transition-colors"
                    onClick={() => handleViewDetails({ userId: otherUser.id })}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleViewDetails({ userId: otherUser.id });
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    {/* 第一行：username | 查看詳情 */}
                    <div className="flex items-center justify-between -mx-6 -mt-6 px-6 pt-6 pb-3 mb-3 border-b border-gray-200 bg-yellow-200 rounded-t-lg">
                      <h3 className="text-lg font-semibold text-gray-900">{otherUser.username}</h3>
                      <div className="flex items-center text-sm text-gray-500">
                        <FiEye className="w-4 h-4 mr-1 hidden md:inline" />
                        <span>查看詳情</span>
                      </div>
                    </div>

                    {/* 第二行：評分 | 接受請求和拒絕請求按鈕（同一行） */}
                    <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200">
                      <span className="text-purple-600 font-semibold">評分: {score}</span>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            handleAcceptRequest(otherUser.id);
                          }}
                          disabled={acceptLoading}
                          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-pink-400 to-purple-500 hover:from-pink-500 hover:to-purple-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                        >
                          <FiCheckCircle className="w-4 h-4 mr-2 hidden md:inline" />
                          {acceptLoading ? '處理中...' : '接受請求'}
                        </button>
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRejectRequest(otherUser.id);
                          }}
                          disabled={rejectLoading}
                          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-500 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                        >
                          <FiXCircle className="w-4 h-4 mr-2 hidden md:inline" />
                          {rejectLoading ? '處理中...' : '拒絕請求'}
                        </button>
                      </div>
                    </div>

                    {/* 第三行：自我介紹前5行 */}
                    <div>
                      {selfIntroPreview ? (
                        <div className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-5">
                          {selfIntroPreview}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-400 italic">暫無自我介紹</div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}
      </div>

      {/* 使用者詳情彈窗 */}
      <UserDetailModal
        isOpen={selectedUserId !== null}
        onClose={handleCloseModal}
        userId={selectedUserId}
        sourceType="matching"
      />
    </div>
  );
};

export default Matching; 